import { LlmTransportError, type TokenUsage } from "../../llm/types.js";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface RetryResult<T> {
  value: T;
  attempts: number;
  failedUsage: TokenUsage;
}

export function addTokenUsage(left: TokenUsage = {}, right: TokenUsage = {}): TokenUsage {
  const sum = (key: keyof TokenUsage): number | undefined =>
    left[key] === undefined && right[key] === undefined
      ? undefined
      : (left[key] ?? 0) + (right[key] ?? 0);
  const inputTokens = sum("inputTokens");
  const outputTokens = sum("outputTokens");
  const cachedInputTokens = sum("cachedInputTokens");
  const cacheCreationTokens = sum("cacheCreationTokens");
  return {
    ...(inputTokens !== undefined ? { inputTokens } : {}),
    ...(outputTokens !== undefined ? { outputTokens } : {}),
    ...(cachedInputTokens !== undefined ? { cachedInputTokens } : {}),
    ...(cacheCreationTokens !== undefined ? { cacheCreationTokens } : {}),
  };
}

export async function withTransientRetry<T>(maxAttempts: number, operation: () => Promise<T>): Promise<RetryResult<T>> {
  let failedUsage: TokenUsage = {};
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return { value: await operation(), attempts: attempt, failedUsage };
    } catch (error) {
      if (error instanceof LlmTransportError) {
        failedUsage = addTokenUsage(failedUsage, error.usage);
        error.attempts = attempt;
        error.usage = failedUsage;
      }
      if (!(error instanceof LlmTransportError) || !error.retryable || attempt === maxAttempts) throw error;
      // Exponential backoff (30s cap) — a shared LAN gateway under load needs the
      // retries spaced out, not hammered within a few seconds.
      await delay(Math.min(30_000, 1000 * 2 ** (attempt - 1)));
    }
  }
  throw new Error("unreachable retry state");
}
